import React, { useEffect, useMemo, useState } from 'react';
import {
  Card, Table, Tag, Input, Button, Space, Typography, Tooltip, Pagination, message, Select, Row, Col,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { getAuditLogOptions, getAuditLogs } from '../services/api';
import { formatDateTime } from '../utils/format';

const { Title, Text } = Typography;

const ALL_FILTER_VALUE = '__all__';

const EMPTY_FILTERS = {
  user_id: undefined,
  action: undefined,
  resource: undefined,
  version_keyword: '',
  keyword: '',
};

const ACTION_COLORS = {
  登录成功: 'green',
  登录失败: 'red',
  登出: 'default',
  新设备登录待审批: 'orange',
  被封锁设备尝试登录: 'red',
  审批设备: 'blue',
  封锁设备: 'red',
  删除设备记录: 'red',
  查看设备列表: 'geekblue',
  扫描版本目录: 'purple',
  创建版本: 'blue',
  更新版本: 'cyan',
  发布版本: 'green',
  删除版本: 'red',
  添加版本备注: 'blue',
  删除版本备注: 'red',
  查看版本列表: 'geekblue',
  查看版本详情: 'geekblue',
  下载版本文件: 'green',
  下载日常版本: 'green',
  从GitLab下载版本: 'green',
  用户注册: 'cyan',
  审批用户: 'green',
  删除用户: 'red',
  修改用户角色: 'blue',
  修改密码: 'orange',
  更新个人信息: 'default',
  同步员工通讯录: 'purple',
};

const RESOURCE_LABELS = {
  auth: '认证',
  version: '版本',
  user: '用户',
  user_device: '设备',
  staff: '员工',
  product: '产品',
};

const VERSION_TYPE_COLORS = {
  release: 'blue',
  daily: 'cyan',
  market: 'orange',
};

function normalizeParams(page, pageSize, filters) {
  const params = { page, pageSize };
  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    const text = String(value).trim();
    if (text) params[key] = text;
  });
  return params;
}

function stringifyDetailValue(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatDetailPreview(value) {
  if (!value) return '';
  try {
    const parsed = JSON.parse(value);
    return Object.entries(parsed)
      .map(([key, child]) => `${key}: ${stringifyDetailValue(child)}`)
      .join(' / ') || value;
  } catch {
    return value;
  }
}

function getAuditTimeParts(value) {
  const full = formatDateTime(value);
  if (!full || full === '-') return { full: '-', date: '-', time: '' };
  const normalized = full.replace(/[,\s]+/g, ' ').trim();
  const [date, ...rest] = normalized.split(' ');
  return {
    full,
    date: date || full,
    time: rest.join(' '),
  };
}

export default function AuditLog() {
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [options, setOptions] = useState({ users: [], actions: [], resources: [] });
  const [pagination, setPagination] = useState({ total: 0, page: 1, pageSize: 50 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const userOptions = useMemo(() => [
    { value: ALL_FILTER_VALUE, label: '全部用户' },
    ...options.users.map((user) => {
      const name = user.name || user.email || `用户#${user.id}`;
      const email = user.email && user.email !== name ? ` <${user.email}>` : '';
      const count = Number.isFinite(user.audit_count) ? ` · ${user.audit_count}条` : '';
      return {
        value: String(user.id),
        label: `${name}${email}${count}`,
      };
    }),
  ], [options.users]);

  const actionOptions = useMemo(() => [
    { value: ALL_FILTER_VALUE, label: '全部操作' },
    ...options.actions.map((item) => ({
      value: item.action,
      label: `${item.action} · ${item.total}`,
    })),
  ], [options.actions]);

  const resourceOptions = useMemo(() => [
    { value: ALL_FILTER_VALUE, label: '全部资源类型' },
    ...options.resources.map((item) => ({
      value: item.resource,
      label: `${item.label || RESOURCE_LABELS[item.resource] || item.resource} · ${item.total}`,
    })),
  ], [options.resources]);

  const fetchOptions = async () => {
    setOptionsLoading(true);
    try {
      const res = await getAuditLogOptions();
      setOptions(res.data.data || { users: [], actions: [], resources: [] });
    } catch (err) {
      message.warning('审计筛选项加载失败，可直接输入关键词查询');
    } finally {
      setOptionsLoading(false);
    }
  };

  const fetchLogs = async (page = 1, pageSize = 50, activeFilters = filters) => {
    setLoading(true);
    try {
      const res = await getAuditLogs(normalizeParams(page, pageSize, activeFilters));
      setData(res.data.data || []);
      setPagination(res.data.pagination || { total: 0, page, pageSize });
    } catch (err) {
      message.error('获取审计日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOptions();
    fetchLogs(1, pagination.pageSize, EMPTY_FILTERS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const handleSelectFilterChange = (key, value) => {
    handleFilterChange(key, value === ALL_FILTER_VALUE ? undefined : value);
  };

  const handleSearch = () => {
    fetchLogs(1, pagination.pageSize, filters);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    fetchLogs(1, pagination.pageSize, EMPTY_FILTERS);
  };

  const renderUser = (_, record) => {
    const name = record.user_name || (record.user_id ? `用户#${record.user_id}` : '系统');
    const title = [name, record.user_email].filter(Boolean).join(' / ');
    return (
      <Tooltip title={title}>
        <div className="audit-user-cell">
          <span>{name}</span>
          {record.user_email ? <Text type="secondary">{record.user_email}</Text> : null}
        </div>
      </Tooltip>
    );
  };

  const renderTarget = (_, record) => {
    const label = record.target_label || record.resource_label || RESOURCE_LABELS[record.resource] || record.resource || '-';
    const meta = record.target_meta || record.target_version_type_label || '';
    return (
      <div className="audit-target-cell">
        <Tooltip title={[label, meta].filter(Boolean).join(' / ')}>
          <span className="audit-target-main">{label}</span>
        </Tooltip>
        <Space size={4} wrap>
          {record.resource ? (
            <Tag className="audit-mini-tag">
              {record.resource_label || RESOURCE_LABELS[record.resource] || record.resource}
            </Tag>
          ) : null}
          {record.target_version_type ? (
            <Tag className="audit-mini-tag" color={VERSION_TYPE_COLORS[record.target_version_type] || 'default'}>
              {record.target_version_type_label || record.target_version_type}
            </Tag>
          ) : null}
          {meta && !record.target_version_type ? <Text type="secondary">{meta}</Text> : null}
        </Space>
      </div>
    );
  };

  const renderDetail = (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    const preview = formatDetailPreview(value);
    return (
      <Tooltip title={<pre style={{ margin: 0, maxWidth: 520, whiteSpace: 'pre-wrap' }}>{preview}</pre>}>
        <span className="audit-detail-cell">{preview}</span>
      </Tooltip>
    );
  };

  const renderAuditTime = (value) => {
    const parts = getAuditTimeParts(value);
    return (
      <Tooltip title={parts.full}>
        <span className="audit-time-cell">
          <span className="audit-time-date">{parts.date}</span>
          {parts.time ? <span className="audit-time-clock">{parts.time}</span> : null}
        </span>
      </Tooltip>
    );
  };

  const renderAuditIp = (value) => (
    <Tooltip title={value || '-'}>
      <code className="audit-ip-cell">{value || '-'}</code>
    </Tooltip>
  );

  const renderAuditFingerprint = (value) => {
    if (!value) return <Text type="secondary">-</Text>;
    return (
      <Tooltip title={value}>
        <code className="audit-fingerprint-cell">{value}</code>
      </Tooltip>
    );
  };

  const columns = [
    {
      title: '时间',
      dataIndex: 'created_at',
      width: 128,
      render: renderAuditTime,
      sorter: (a, b) => new Date(`${a.created_at}Z`) - new Date(`${b.created_at}Z`),
      defaultSortOrder: 'descend',
    },
    {
      title: '操作人',
      dataIndex: 'user_name',
      width: 154,
      render: renderUser,
    },
    {
      title: '操作',
      dataIndex: 'action',
      width: 136,
      render: (v) => <Tag color={ACTION_COLORS[v] || 'default'}>{v}</Tag>,
    },
    {
      title: '操作对象',
      dataIndex: 'target_label',
      width: 242,
      render: renderTarget,
    },
    {
      title: '详情',
      dataIndex: 'detail',
      ellipsis: false,
      render: renderDetail,
    },
    { title: 'IP地址', dataIndex: 'ip', width: 146, ellipsis: false, render: renderAuditIp },
    {
      title: '设备指纹',
      dataIndex: 'fingerprint',
      width: 136,
      ellipsis: false,
      render: renderAuditFingerprint,
    },
  ];

  return (
    <div className="page-shell">
      <div className="page-heading">
        <div>
          <div className="page-kicker">Audit Trail</div>
          <Title level={3} className="page-heading-title">审计日志</Title>
          <Text className="page-heading-subtitle">按用户、操作类型和版本对象追踪系统关键行为。</Text>
        </div>
      </div>

      <Card className="toolbar-card audit-filter-card">
        <Row gutter={[10, 10]} align="middle">
          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              allowClear
              showSearch
              loading={optionsLoading}
              placeholder="选择用户"
              value={filters.user_id ?? ALL_FILTER_VALUE}
              options={userOptions}
              optionFilterProp="label"
              onChange={(value) => handleSelectFilterChange('user_id', value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              allowClear
              showSearch
              loading={optionsLoading}
              placeholder="选择操作"
              value={filters.action ?? ALL_FILTER_VALUE}
              options={actionOptions}
              optionFilterProp="label"
              onChange={(value) => handleSelectFilterChange('action', value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              loading={optionsLoading}
              placeholder="资源类型"
              value={filters.resource ?? ALL_FILTER_VALUE}
              options={resourceOptions}
              onChange={(value) => handleSelectFilterChange('resource', value)}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="版本号 / 产品"
              value={filters.version_keyword}
              onChange={(e) => handleFilterChange('version_keyword', e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              placeholder="全文关键词"
              value={filters.keyword}
              onChange={(e) => handleFilterChange('keyword', e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={4} lg={1}>
            <Button block type="primary" icon={<SearchOutlined />} onClick={handleSearch} />
          </Col>
          <Col xs={12} sm={6} md={4} lg={1}>
            <Button block icon={<ReloadOutlined />} onClick={handleReset} />
          </Col>
        </Row>
      </Card>

      <Card className="content-card audit-log-card">
        <Table
          className="fit-table audit-log-table"
          size="small"
          tableLayout="fixed"
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={false}
        />
        <div className="audit-pagination">
          <Pagination
            current={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            showTotal={(total) => `共 ${total} 条`}
            onChange={(page, pageSize) => fetchLogs(page, pageSize, filters)}
          />
        </div>
      </Card>
    </div>
  );
}
